import {
  Component,
  type ComponentType,
  type ErrorInfo,
  lazy,
  type LazyExoticComponent,
  type ReactNode,
  Suspense,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useGetResourceDetailQuery, useGetResourceTypeSchemaQuery } from '../store/hooks';
import { ResourceActivityCard } from './resource-activity-card';
import {
  resolveResourceDetailViewMode,
  type ResourceDetailViewMode,
} from '../utils/resource-explorer-url';

import type { ResourceRendererProps } from './resource-renderer-types';

const NoteResourcePreview = lazy(() =>
  import('./resource-renderers/note-resource-preview').then(module => ({
    default: module.NoteResourcePreview,
  })),
);

const TranscriptionResourcePreview = lazy(() =>
  import('./resource-renderers/transcription-resource-preview').then(module => ({
    default: module.TranscriptionResourcePreview,
  })),
);

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isIsoDate(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v);
}

/** Recursively render an arbitrary JSON value from a resource payload. */
function ResourceValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return (
      <Typography component="span" color="text.secondary">
        —
      </Typography>
    );
  }
  if (typeof value === 'boolean') {
    return (
      <Chip size="small" label={value ? 'true' : 'false'} color={value ? 'success' : 'default'} />
    );
  }
  if (typeof value === 'number') {
    return <span>{value}</span>;
  }
  if (typeof value === 'string') {
    return <span>{isIsoDate(value) ? new Date(value).toLocaleString() : value}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography component="span" color="text.secondary">
          (empty)
        </Typography>
      );
    }
    return (
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {(value as unknown[]).map((item, i) => (
          <li key={i}>
            <ResourceValue value={item} />
          </li>
        ))}
      </Box>
    );
  }
  if (typeof value === 'object') {
    return <KeyValueTable obj={value as Record<string, unknown>} />;
  }
  return null;
}

function KeyValueTable({
  obj,
  labels,
}: {
  obj: Record<string, unknown>;
  labels?: Record<string, string>;
}) {
  const keys = Object.keys(obj);
  if (keys.length === 0) {
    return (
      <Typography component="span" color="text.secondary">
        (empty)
      </Typography>
    );
  }
  return (
    <Table
      size="small"
      sx={{
        '& td': {
          borderBottom: '1px solid',
          borderColor: 'divider',
          verticalAlign: 'top',
        },
      }}
    >
      <TableBody>
        {keys.map(k => (
          <TableRow key={k}>
            <TableCell sx={{ width: 240, fontWeight: 500, color: 'text.secondary' }}>
              {labels?.[k] ?? humanize(k)}
            </TableCell>
            <TableCell>
              <ResourceValue value={obj[k]} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Default renderer: labeled key/value driven by the resource's JSON Schema. */
function SchemaResourceView({ data, schema }: ResourceRendererProps) {
  const props = schema?.json_schema?.properties ?? {};
  const labels: Record<string, string> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v && typeof v.title === 'string') labels[k] = v.title;
  }
  return <KeyValueTable obj={data} labels={labels} />;
}

/**
 * Renderer registry: `resource_type → component`. Per-type custom renderers
 * register here (GDN-1923); everything falls back to the schema-driven default.
 */
const RESOURCE_RENDERERS: Record<
  string,
  LazyExoticComponent<ComponentType<ResourceRendererProps>>
> = {
  note: NoteResourcePreview,
  transcription_session: TranscriptionResourcePreview,
};

interface ResourcePreviewErrorBoundaryProps {
  children: ReactNode;
}

interface ResourcePreviewErrorBoundaryState {
  hasError: boolean;
}

class ResourcePreviewErrorBoundary extends Component<
  ResourcePreviewErrorBoundaryProps,
  ResourcePreviewErrorBoundaryState
> {
  state: ResourcePreviewErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ResourcePreviewErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Resource preview renderer failed', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Typography color="error">
          This preview could not be rendered. Fields and Raw JSON are still available.
        </Typography>
      );
    }

    return this.props.children;
  }
}

interface ResourceDetailViewProps {
  resourceType: string;
  resourceId: string;
  organizationId?: string;
}

/**
 * Reads one resource via the governed `Resources` registry and renders it with
 * the per-type renderer (or the schema-driven default), plus a Raw JSON toggle.
 */
export function ResourceDetailView({
  resourceType,
  resourceId,
  organizationId,
}: ResourceDetailViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: detail,
    isLoading,
    error,
  } = useGetResourceDetailQuery({
    resourceType,
    resourceId,
    organizationId,
  });
  const { data: schema } = useGetResourceTypeSchemaQuery({ resourceType });

  const data = (detail?.data ?? {}) as Record<string, unknown>;
  const PreviewRenderer = RESOURCE_RENDERERS[resourceType];
  const hasPreview = Boolean(PreviewRenderer);
  const mode = resolveResourceDetailViewMode(searchParams.get('rview'), hasPreview);
  const availableModes: ResourceDetailViewMode[] = hasPreview
    ? ['preview', 'fields', 'raw']
    : ['fields', 'raw'];

  const setMode = (nextMode: ResourceDetailViewMode | null) => {
    if (!nextMode) return;
    const next = new URLSearchParams(searchParams);
    next.set('rview', nextMode);
    setSearchParams(next);
  };

  return (
    <>
    <Card>
      <CardHeader
        title={resourceType}
        subheader={resourceId}
        action={
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_event, nextMode: ResourceDetailViewMode | null) => setMode(nextMode)}
            aria-label="Resource detail view"
          >
            {availableModes.map(availableMode => (
              <ToggleButton key={availableMode} value={availableMode}>
                {availableMode === 'raw'
                  ? 'Raw JSON'
                  : availableMode[0].toUpperCase() + availableMode.slice(1)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        }
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error">
            Could not load this resource (it may not exist or be out of scope).
          </Typography>
        ) : mode === 'raw' ? (
          <pre
            style={{
              margin: 0,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {JSON.stringify(data, null, 2)}
          </pre>
        ) : mode === 'preview' && PreviewRenderer ? (
          <ResourcePreviewErrorBoundary key={`${resourceType}:${resourceId}`}>
            <Suspense
              fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              }
            >
              <PreviewRenderer
                key={`${resourceType}:${resourceId}`}
                data={data}
                schema={schema}
                resourceType={resourceType}
                resourceId={resourceId}
                organizationId={organizationId}
              />
            </Suspense>
          </ResourcePreviewErrorBoundary>
        ) : (
          <SchemaResourceView
            data={data}
            schema={schema}
            resourceType={resourceType}
            resourceId={resourceId}
            organizationId={organizationId}
          />
        )}
      </CardContent>
    </Card>
    {organizationId && (
      <ResourceActivityCard
        resourceType={resourceType}
        resourceId={resourceId}
        organizationId={organizationId}
      />
    )}
    </>
  );
}
